import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Heart, UserPlus, Image as ImageIcon, CheckCircle2, XCircle, Clock,
  Calendar, ExternalLink, Upload, Video, Zap, Plus, Copy, FileText, X,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { useAuth } from "../../lib/auth";

function AdminGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

const TABS = [
  { v: "applications", l: "Applications" },
  { v: "sessions", l: "Sessions" },
  { v: "branding", l: "Branding" },
];

export default function AdminTherapyPage() {
  const [tab, setTab] = useState("applications");
  const [showInstant, setShowInstant] = useState(false);
  return (
    <AdminGate>
      <div data-testid="admin-therapy-page" className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Admin · Therapy</p>
            <h1 className="font-serif font-light text-4xl">Therapy Sessions</h1>
            <p className="text-[var(--muted)] text-sm mt-2">
              Approve therapists, monitor sessions, and start meetings — all in one place.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowInstant(true)}
              className="text-sm px-4 py-3 rounded-xl border border-[#C8A96B]/40 hover:bg-[#C8A96B]/10 flex items-center gap-2"
              data-testid="admin-therapy-start-instant-btn">
              <Zap className="w-4 h-4 text-[var(--gold)]" /> Start instant session
            </button>
            <Link to="/dashboard/therapy" className="btn-gold text-sm" data-testid="admin-therapy-hub-link">
              <Plus className="w-4 h-4" /> Schedule a session
            </Link>
          </div>
        </div>
        <div className="flex gap-2 border-b border-[var(--line)]">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-[2px] ${
                tab === t.v ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-black/50 hover:text-black"
              }`}
              data-testid={`admin-therapy-tab-${t.v}`}
            >{t.l}</button>
          ))}
        </div>
        {tab === "applications" && <ApplicationsTab />}
        {tab === "sessions" && <SessionsTab />}
        {tab === "branding" && <BrandingTab />}
        {showInstant && <InstantModal onClose={() => setShowInstant(false)} />}
      </div>
    </AdminGate>
  );
}

/* ---- Instant session modal (admin/therapist) ---- */
function InstantModal({ onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [duration, setDuration] = useState(50);
  const [skipIntake, setSkipIntake] = useState(true);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function start() {
    if (!email.trim() || !email.includes("@")) return toast.error("Client email required.");
    setBusy(true);
    try {
      const r = await api.post("/therapy/instant", {
        client_email: email.trim(), client_name: name.trim() || undefined,
        duration_minutes: Number(duration), skip_intake: skipIntake,
      });
      setCreated(r.data);
      toast.success("Instant session created.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  function copyLink() {
    if (!created) return;
    const url = `${window.location.origin}${created.magic_link_path}`;
    navigator.clipboard.writeText(url);
    toast.success("Magic link copied");
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-therapy-instant-modal">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif text-3xl flex items-center gap-2">
            <Zap className="w-6 h-6 text-[var(--gold)]" /> Start now
          </h2>
          <button onClick={onClose} className="text-black/40"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-black/60 mb-4">Spin up a room immediately. We'll give you a magic link to share.</p>
        {created ? (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-sm font-medium">Room ready ✓</p>
              <code className="block bg-white px-3 py-2 rounded-lg text-xs mt-2 break-all border border-[var(--line)]">
                {window.location.origin}{created.magic_link_path}
              </code>
            </div>
            <div className="flex gap-2">
              <button onClick={copyLink} className="flex-1 text-sm px-4 py-3 rounded-xl border border-[var(--line)] hover:bg-[#C8A96B]/10"
                data-testid="admin-instant-copy"><Copy className="w-4 h-4 inline mr-1" /> Copy link</button>
              <Link to={created.magic_link_path} className="btn-gold flex-1" data-testid="admin-instant-enter">
                <Video className="w-4 h-4" /> Start session
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-black/50">Client email *</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@email.com"
                  className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
                  data-testid="admin-instant-email" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-black/50">Client name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional"
                  className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
                  data-testid="admin-instant-name" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-black/50">Duration</label>
                <select value={duration} onChange={(e) => setDuration(e.target.value)}
                  className="w-full mt-1 px-4 py-3 rounded-xl border border-[var(--line)]" data-testid="admin-instant-duration">
                  <option value={30}>30 min</option>
                  <option value={50}>50 min</option>
                  <option value={90}>90 min</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={skipIntake} onChange={(e) => setSkipIntake(e.target.checked)}
                  className="accent-[var(--gold)]" data-testid="admin-instant-skip-intake" />
                Skip intake form (impromptu / returning client)
              </label>
            </div>
            <button onClick={start} disabled={busy} className="btn-gold w-full mt-4" data-testid="admin-instant-submit">
              <Zap className="w-4 h-4" /> {busy ? "Creating…" : "Create instant room"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ApplicationsTab() {
  const [rows, setRows] = useState([]);
  const [grantEmail, setGrantEmail] = useState("");

  async function load() {
    try {
      const r = await api.get("/admin/therapist-applications");
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  async function decide(aid, decision) {
    try {
      await api.post(`/admin/therapist-applications/${aid}/${decision}`);
      toast.success(`Application ${decision}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function grantByEmail() {
    if (!grantEmail.trim()) return;
    try {
      const r = await api.get("/admin/users");
      const u = (r.data || []).find((x) => (x.email || "").toLowerCase() === grantEmail.trim().toLowerCase());
      if (!u) return toast.error("User not found.");
      await api.post(`/admin/users/${u.user_id}/grant-therapist`);
      toast.success(`${u.email} is now a therapist.`);
      setGrantEmail("");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[var(--line)] rounded-2xl p-6" data-testid="admin-therapy-grant">
        <h3 className="font-serif text-2xl flex items-center gap-2 mb-3">
          <UserPlus className="w-5 h-5 text-[var(--gold)]" /> Grant therapist role to existing user
        </h3>
        <div className="flex gap-3 flex-wrap">
          <input value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)}
            placeholder="user@example.com" type="email"
            className="flex-1 min-w-[260px] px-4 py-3 rounded-xl border border-[var(--line)] outline-none focus:border-[var(--gold)]"
            data-testid="admin-therapy-grant-email" />
          <button onClick={grantByEmail} className="btn-gold" data-testid="admin-therapy-grant-btn">Grant</button>
        </div>
      </div>

      <div className="space-y-2" data-testid="admin-therapy-apps-list">
        {rows.length === 0 ? (
          <p className="text-sm text-black/40 text-center py-12">No applications yet.</p>
        ) : rows.map((a) => (
          <div key={a.application_id} className="bg-white border border-[var(--line)] rounded-xl p-5"
            data-testid={`therapy-app-row-${a.application_id}`}>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg">{a.user_name} <span className="text-xs text-black/40">{a.user_email}</span></p>
                <p className="text-xs text-black/50 mt-1">License #{a.license_number} ({a.license_state}) · ${a.hourly_rate}/hr</p>
                <p className="text-xs text-black/40 mt-1">Specialties: {(a.specialties || []).join(", ") || "—"}</p>
                <p className="text-sm text-black/70 mt-2 italic">"{a.bio?.slice(0, 200)}{a.bio?.length > 200 ? "…" : ""}"</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                  a.status === "approved" ? "bg-emerald-100 text-emerald-700"
                  : a.status === "rejected" ? "bg-red-100 text-red-700"
                  : "bg-amber-100 text-amber-700"
                }`}>{a.status}</span>
                {a.status === "pending" && (
                  <div className="flex gap-2">
                    <button onClick={() => decide(a.application_id, "approve")}
                      className="text-xs px-3 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`therapy-app-approve-${a.application_id}`}>
                      <CheckCircle2 className="w-3 h-3 inline mr-1" /> Approve
                    </button>
                    <button onClick={() => decide(a.application_id, "reject")}
                      className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      data-testid={`therapy-app-reject-${a.application_id}`}>
                      <XCircle className="w-3 h-3 inline mr-1" /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SessionsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/therapy/sessions").then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  function copyLink(sid) {
    navigator.clipboard.writeText(`${window.location.origin}/therapy/session/${sid}`);
    toast.success("Magic link copied");
  }
  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-6" data-testid="admin-therapy-sessions">
      <h3 className="font-serif text-2xl mb-4">All sessions</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-black/40">No sessions yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-black/50">
              <tr className="text-left">
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Therapist</th>
                <th className="py-2 pr-3">Client</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const cancelled = s.status === "cancelled";
                const past = (new Date(s.starts_at).getTime() - Date.now()) / 60000 < -10;
                const canStart = !cancelled && !past;
                return (
                <tr key={s.session_id} className="border-t border-[var(--line)]" data-testid={`session-row-${s.session_id}`}>
                  <td className="py-2 pr-3 text-xs">{(s.starts_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 pr-3">{s.therapist_name}</td>
                  <td className="py-2 pr-3">{s.client_name || s.client_email}</td>
                  <td className="py-2 pr-3">${(s.amount || 0).toFixed(2)}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
                      cancelled ? "bg-black/10 text-black/50"
                      : past ? "bg-emerald-50 text-emerald-700"
                      : s.payment_status === "paid" || s.payment_status === "complimentary" ? "bg-emerald-100 text-emerald-700"
                      : "bg-amber-100 text-amber-700"
                    }`}>{cancelled ? "cancelled" : past ? "past" : (s.payment_status || s.status)}</span>
                  </td>
                  <td className="py-2 text-right">
                    <div className="inline-flex gap-1.5">
                      {canStart && (
                        <Link to={`/therapy/session/${s.session_id}`}
                          className="text-xs px-3 py-1.5 rounded-lg bg-[var(--gold)] text-white hover:opacity-90 inline-flex items-center gap-1"
                          data-testid={`admin-session-start-${s.session_id}`}>
                          <Video className="w-3 h-3" /> Start session
                        </Link>
                      )}
                      {past && !cancelled && (
                        <Link to={`/dashboard/therapy/notes/${s.session_id}`}
                          className="text-xs px-3 py-1.5 rounded-lg border border-[var(--line)] hover:bg-[#C8A96B]/10 inline-flex items-center gap-1"
                          data-testid={`admin-session-notes-${s.session_id}`}>
                          <FileText className="w-3 h-3" /> Notes
                        </Link>
                      )}
                      {!cancelled && (
                        <button onClick={() => copyLink(s.session_id)}
                          className="text-xs p-1.5 rounded-lg border border-[var(--line)] hover:bg-[#C8A96B]/10"
                          title="Copy magic link" data-testid={`admin-session-copy-${s.session_id}`}>
                          <Copy className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BrandingTab() {
  const [settings, setSettings] = useState({});
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await api.get("/therapy/settings");
      setSettings(r.data || {});
      setUrl(r.data?.thumbnail_url || "");
    } catch (e) { /* ignore */ }
  }
  useEffect(() => { load(); }, []);

  async function uploadFile(file) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Max 10MB");
    setBusy(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/admin/therapy/thumbnail", { photo_data_url: reader.result });
        toast.success("Thumbnail uploaded.");
        load();
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  }

  async function saveUrl() {
    if (!url) return;
    setBusy(true);
    try {
      await api.post("/admin/therapy/thumbnail", { photo_url: url });
      toast.success("Thumbnail URL saved.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  const tn = settings.thumbnail_url
    ? (settings.thumbnail_url.startsWith("http") ? settings.thumbnail_url : `${BACKEND_URL}${settings.thumbnail_url}`)
    : null;

  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-6" data-testid="admin-therapy-branding">
      <h3 className="font-serif text-2xl flex items-center gap-2 mb-3">
        <ImageIcon className="w-5 h-5 text-[var(--gold)]" /> Therapy thumbnail / hero photo
      </h3>
      <p className="text-sm text-black/60 mb-4">
        Used on the public therapy landing page and as the meeting room avatar.
      </p>
      {tn && (
        <img src={tn} alt="thumbnail" className="w-48 h-48 object-cover rounded-xl mb-4 border border-[var(--line)]" data-testid="therapy-thumbnail-preview" />
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border border-dashed border-[var(--line)] rounded-xl p-4 text-center">
          <Upload className="w-6 h-6 mx-auto mb-2 text-[var(--gold)]" />
          <p className="text-xs text-black/50 mb-2">Upload from device</p>
          <input type="file" accept="image/*" onChange={(e) => uploadFile(e.target.files?.[0])}
            disabled={busy} className="text-xs" data-testid="therapy-thumbnail-file" />
        </div>
        <div className="border border-[var(--line)] rounded-xl p-4">
          <p className="text-xs text-black/50 mb-2">Or paste an image URL</p>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..."
            className="w-full px-3 py-2 rounded-lg border border-[var(--line)] text-sm mb-2"
            data-testid="therapy-thumbnail-url" />
          <button onClick={saveUrl} disabled={busy || !url} className="btn-gold w-full" data-testid="therapy-thumbnail-save">Save URL</button>
        </div>
      </div>
    </div>
  );
}
